<template>
  <div class="km-thread">
    <!-- ===== TOP BAR ===== -->
    <header class="kt-top">
      <button class="kt-back" @click="goBack" aria-label="Back">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
      </button>

      <button class="kt-peer" @click="toggleInfo">
        <div class="kt-peer__avatar">
          <img :src="peer.avatar" :alt="peer.name"/>
          <span v-if="peer.online" class="kt-peer__dot"></span>
        </div>
        <div class="kt-peer__body">
          <div class="kt-peer__name">
            {{ peer.name }}
            <span v-if="peer.verified" class="kt-peer__tick">✓</span>
          </div>
          <div class="kt-peer__status">
            <span v-if="peerTyping" class="kt-typing-ind">
              <span></span><span></span><span></span> typing…
            </span>
            <span v-else-if="peer.online">Online now</span>
            <span v-else>Last seen {{ peer.lastSeen }}</span>
          </div>
        </div>
      </button>

      <div class="kt-actions">
        <button class="kt-icon" @click="soon" aria-label="Voice call">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z"/></svg>
        </button>
        <button class="kt-icon" @click="soon" aria-label="Video call">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
        </button>
        <button class="kt-icon" @click="toggleInfo" aria-label="More">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg>
        </button>
      </div>
    </header>

    <!-- ===== MESSAGES AREA ===== -->
    <main class="kt-messages" ref="msgs">
      <!-- Match celebration banner at the very top -->
      <div class="kt-match-banner">
        <div class="kt-match-banner__hearts">💖</div>
        <div>
          <strong>It's a match with {{ peer.name }}!</strong>
          <small>You both liked each other on {{ peer.matchedOn }}. Break the ice ✨</small>
        </div>
      </div>

      <template v-for="(block, bi) in groupedMessages">
        <!-- Date divider -->
        <div class="kt-date" :key="'d'+bi">
          <span>{{ block.date }}</span>
        </div>

        <!-- Messages in this date block -->
        <div
          v-for="(m, mi) in block.messages" :key="'m'+bi+'-'+mi"
          class="kt-row"
          :class="[m.mine ? 'kt-row--mine' : 'kt-row--theirs', { 'kt-row--tail': m.showTail, 'kt-row--first': m.showAvatar }]"
        >
          <div v-if="!m.mine && m.showAvatar" class="kt-row__avatar">
            <img :src="peer.avatar" :alt="peer.name"/>
          </div>
          <div v-else-if="!m.mine" class="kt-row__avatar kt-row__avatar--ghost"></div>

          <div class="kt-bubble-wrap">
            <div class="kt-bubble">
              <span v-if="m.kind === 'gift'" class="kt-bubble__gift">
                <span class="kt-bubble__gift-emoji">{{ m.gift }}</span>
                <span>{{ m.text }}</span>
              </span>
              <span v-else-if="m.kind === 'image'" class="kt-bubble__img">
                <img :src="m.src" :alt="m.text || 'photo'"/>
              </span>
              <span v-else>{{ m.text }}</span>
            </div>
            <div v-if="m.showTime" class="kt-bubble__meta">
              {{ m.time }}
              <span v-if="m.mine && m.read" class="kt-bubble__read">✓✓</span>
              <span v-else-if="m.mine" class="kt-bubble__sent">✓</span>
            </div>
          </div>
        </div>
      </template>

      <!-- Live typing indicator -->
      <div v-if="peerTyping" class="kt-row kt-row--theirs kt-row--first">
        <div class="kt-row__avatar"><img :src="peer.avatar" alt=""/></div>
        <div class="kt-bubble-wrap">
          <div class="kt-bubble kt-bubble--typing">
            <span class="kt-typing"><span></span><span></span><span></span></span>
          </div>
        </div>
      </div>
    </main>

    <!-- ===== COMPOSER ===== -->
    <footer class="kt-composer">
      <button class="kt-icon kt-icon--attach" @click="soon" aria-label="Attach">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05 12.25 20.24a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.49"/></svg>
      </button>

      <button class="kt-icon kt-icon--gift" @click="sendGift" aria-label="Send gift">
        🎁
      </button>

      <div class="kt-input" :class="{ 'has-text': draft.length > 0 }">
        <textarea
          ref="input"
          v-model="draft"
          rows="1"
          :placeholder="'Message ' + peer.name + '…'"
          @keydown="onKeydown"
          @input="onInput"
        ></textarea>
        <button class="kt-emoji" @click="toggleEmoji" aria-label="Emojis">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm-3 8a1.5 1.5 0 1 1 1.5 1.5A1.5 1.5 0 0 1 9 10Zm6 0a1.5 1.5 0 1 1 1.5 1.5A1.5 1.5 0 0 1 15 10Zm-3 8a5 5 0 0 1-4.58-3h9.16A5 5 0 0 1 12 18Z"/>
          </svg>
        </button>
      </div>

      <button v-if="draft.trim()" class="kt-send" @click="sendMessage" aria-label="Send">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M2 21l21-9L2 3v7l15 2-15 2z"/></svg>
      </button>
      <button v-else class="kt-send kt-send--mic" @click="soon" aria-label="Voice note">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Zm5.3-3a5.3 5.3 0 0 1-10.6 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11Z"/></svg>
      </button>
    </footer>

    <!-- Quick emoji sheet -->
    <transition name="kt-emoji-sheet">
      <div v-if="showEmoji" class="kt-emoji-sheet" @click.self="showEmoji = false">
        <div class="kt-emoji-sheet__body">
          <div class="kt-emoji-sheet__head">
            <strong>Quick emojis</strong>
            <button @click="showEmoji = false">✕</button>
          </div>
          <div class="kt-emoji-sheet__grid">
            <button v-for="e in quickEmojis" :key="e" @click="insertEmoji(e)">{{ e }}</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Profile info drawer -->
    <transition name="kt-info">
      <div v-if="showInfo" class="kt-info" @click.self="showInfo = false">
        <div class="kt-info__panel">
          <button class="kt-info__close" @click="showInfo = false">✕</button>
          <img :src="peer.avatar" class="kt-info__avatar" alt=""/>
          <div class="kt-info__name">{{ peer.name }}<span v-if="peer.verified" class="kt-info__tick">✓</span></div>
          <div class="kt-info__meta">📍 {{ peer.city }} · {{ peer.distance }}</div>
          <p class="kt-info__bio">{{ peer.bio }}</p>
          <div class="kt-info__actions">
            <button class="kt-info__btn" @click="soon">👤 View profile</button>
            <button class="kt-info__btn" @click="soon">🔔 Mute</button>
            <button class="kt-info__btn kt-info__btn--danger" @click="soon">🚫 Report &amp; unmatch</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
const NAMES = ['Sophia','Emma','Aiko','Maya','Noelle','Priya','Lila','Zoe','Chloe','Ava','Ruby','Luna'];
const CITIES = ['New York','Los Angeles','London','Paris','Tokyo','Seoul','Sydney','Madrid','Berlin','Dubai'];
const AVATARS = [
  'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop&crop=faces',
  'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop&crop=faces',
  'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&h=200&fit=crop&crop=faces',
  'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop&crop=faces',
  'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=200&h=200&fit=crop&crop=faces',
  'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=200&h=200&fit=crop&crop=faces'
];

// Sample conversation seeded per id so each chat feels different but deterministic
function seedMessages(seed){
  const now = Date.now();
  const rng = (n) => Math.abs(Math.sin(seed * 0.7 + n * 1.3) * 1e6) % 1;
  const samples = [
    { mine:false, text:'Hey! Really loved your profile ✨' },
    { mine:true,  text:'Aw thank you! Yours made me smile too 😊' },
    { mine:false, text:'So what do you do for fun around here?' },
    { mine:true,  text:'Coffee shops mostly. And getting lost in bookstores.' },
    { mine:false, text:'Same — I have a whole list of spots we should try.' },
    { mine:true,  text:'Sold. Send them over.' },
    { mine:false, kind:'gift', gift:'🌹', text:'Sent you a Rose' },
    { mine:true,  text:'Haha smooth 😂' },
    { mine:false, text:'Are you free Saturday?' },
    { mine:true,  text:'Yeah! Morning or afternoon?' },
    { mine:false, text:'Afternoon, say 3pm?' }
  ];
  const out = [];
  samples.forEach((s, i) => {
    const offset = (samples.length - i) * 45 * 60 * 1000 - Math.floor(rng(i) * 30 * 60 * 1000);
    out.push({
      ...s,
      at: now - offset,
      read: i < samples.length - 2,
      kind: s.kind || 'text'
    });
  });
  return out;
}

function hashId(id){
  const s = String(id || '');
  let h = 0;
  for(let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export default {
  name: 'ChatThread',
  data(){
    return {
      draft: '',
      showEmoji: false,
      showInfo: false,
      peerTyping: false,
      messages: [],
      peer: {
        name: 'Sophia',
        avatar: AVATARS[0],
        online: true,
        verified: true,
        lastSeen: '2 h ago',
        city: 'Paris',
        distance: '4 km away',
        bio: 'Coffee dates > dinner dates. Change my mind ☕',
        matchedOn: 'Tuesday'
      },
      quickEmojis: ['😂','❤️','😍','🔥','😊','🙈','🥰','💕','✨','🎉','🌹','💋','😎','🤔','👀','💯','🙌','💖','🌸','🍷','🥂','☕','🎵','📸']
    };
  },
  computed: {
    groupedMessages(){
      const msgs = this.messages.slice();
      if(!msgs.length) return [];
      const groups = [];
      let curDay = '';
      let group = null;
      msgs.forEach((m, i) => {
        const d = new Date(m.at);
        const dayLabel = this.dayLabel(d);
        if(dayLabel !== curDay){
          curDay = dayLabel;
          group = { date: dayLabel, messages: [] };
          groups.push(group);
        }
        const prev = i > 0 ? msgs[i-1] : null;
        const next = i < msgs.length - 1 ? msgs[i+1] : null;
        const showAvatar = !m.mine && (!prev || prev.mine !== m.mine || dayLabel !== this.dayLabel(new Date(prev.at)));
        const showTail = !next || next.mine !== m.mine || (m.at && next.at && next.at - m.at > 5 * 60 * 1000);
        const showTime = showTail;
        group.messages.push({ ...m, showAvatar, showTail, showTime, time: this.timeLabel(d) });
      });
      return groups;
    }
  },
  methods: {
    dayLabel(d){
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      const that = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      const diff = Math.round((today - that) / 86400000);
      if(diff === 0) return 'Today';
      if(diff === 1) return 'Yesterday';
      if(diff < 7) return d.toLocaleDateString(undefined, { weekday: 'long' });
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    },
    timeLabel(d){
      return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    },
    goBack(){ this.$router.push('/Chats'); },
    soon(){ this.$toast('Coming soon ✨'); },
    sendGift(){
      this.messages.push({
        mine: true, kind: 'gift', gift: '🌹', text: 'Sent a Rose',
        at: Date.now(), read: false
      });
      this.scrollBottom();
    },
    toggleEmoji(){ this.showEmoji = !this.showEmoji; this.showInfo = false; },
    toggleInfo(){ this.showInfo = !this.showInfo; this.showEmoji = false; },
    insertEmoji(e){
      this.draft += e;
      this.$refs.input && this.$refs.input.focus();
    },
    onInput(e){
      const el = e.target;
      el.style.height = 'auto';
      el.style.height = Math.min(120, el.scrollHeight) + 'px';
    },
    onKeydown(e){
      if(e.key === 'Enter' && !e.shiftKey){
        e.preventDefault();
        this.sendMessage();
      }
    },
    sendMessage(){
      const text = this.draft.trim();
      if(!text) return;
      this.messages.push({
        mine: true, kind: 'text', text,
        at: Date.now(), read: false
      });
      this.draft = '';
      if(this.$refs.input){ this.$refs.input.style.height = 'auto'; }
      this.scrollBottom();
      // Simulate the peer typing and replying
      this.fakeReply();
    },
    fakeReply(){
      clearTimeout(this._typingTimer);
      this._typingTimer = setTimeout(() => {
        this.peerTyping = true;
        this.scrollBottom();
      }, 700);
      clearTimeout(this._replyTimer);
      this._replyTimer = setTimeout(() => {
        this.peerTyping = false;
        const replies = [
          'Hmm let me think 🤔',
          'Sounds good to me ✨',
          'Haha yes!',
          'Tell me more',
          'I was just thinking that',
          '😂😂😂',
          'Deal.',
          'You\'re funny, you know that?'
        ];
        const pick = replies[Math.floor(Math.random() * replies.length)];
        this.messages.push({ mine: false, kind: 'text', text: pick, at: Date.now(), read: true });
        this.scrollBottom();
        // Mark earlier mine-messages as read
        this.messages.forEach(m => { if(m.mine) m.read = true; });
      }, 2600);
    },
    scrollBottom(){
      this.$nextTick(() => {
        const el = this.$refs.msgs;
        if(el) el.scrollTop = el.scrollHeight;
      });
    }
  },
  created(){
    if(!localStorage.getItem('token')){ this.$router.replace('/Login'); return; }
    const id = this.$route.query.id || this.$route.params.id || '1';
    const seed = hashId(id);
    this.peer = {
      ...this.peer,
      name: NAMES[seed % NAMES.length],
      avatar: AVATARS[seed % AVATARS.length],
      city: CITIES[seed % CITIES.length],
      distance: (1 + (seed % 20)) + ' km away',
      online: seed % 3 !== 0,
      verified: seed % 4 !== 0,
      lastSeen: ['2 min','15 min','1 h','3 h','today'][seed % 5],
      matchedOn: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'][seed % 7]
    };
    this.messages = seedMessages(seed);
  },
  mounted(){ this.scrollBottom(); },
  beforeDestroy(){
    clearTimeout(this._typingTimer);
    clearTimeout(this._replyTimer);
  }
};
</script>

<style scoped>
.km-thread {
  position: fixed; inset: 0; z-index: 10;
  display: flex; flex-direction: column;
  background: linear-gradient(180deg, #fdf5f9 0%, #f6eef3 100%);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, "PingFang SC", sans-serif;
  color: #2a1a3f;
  overflow: hidden;
}
.km-thread * { box-sizing: border-box; }
button { font-family: inherit; cursor: pointer; }

/* ===== Top bar ===== */
.kt-top {
  flex-shrink: 0;
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px;
  background: rgba(255,255,255,.9);
  backdrop-filter: blur(22px) saturate(170%);
  -webkit-backdrop-filter: blur(22px) saturate(170%);
  border-bottom: 1px solid rgba(255,77,141,.1);
}
.kt-back {
  width: 40px; height: 40px; border-radius: 12px; border: 1px solid rgba(26,16,51,.08);
  background: #fff; color: #2a1a3f;
  display: flex; align-items: center; justify-content: center;
  transition: border-color .2s, transform .15s;
}
.kt-back:hover { border-color: #ff4d8d; transform: translateY(-1px); }

.kt-peer {
  flex: 1; min-width: 0;
  display: flex; align-items: center; gap: 10px;
  background: none; border: none; padding: 4px 8px; text-align: left; cursor: pointer;
  border-radius: 14px; transition: background .15s;
}
.kt-peer:hover { background: rgba(255,77,141,.05); }
.kt-peer__avatar { position: relative; width: 42px; height: 42px; flex-shrink: 0; }
.kt-peer__avatar img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; }
.kt-peer__dot {
  position: absolute; bottom: 0; right: 0;
  width: 11px; height: 11px; border-radius: 50%;
  background: #10b981; border: 2px solid #fff;
}
.kt-peer__body { flex: 1; min-width: 0; }
.kt-peer__name {
  font-size: 15px; font-weight: 800; color: #2a1a3f;
  display: flex; align-items: center; gap: 4px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.kt-peer__tick {
  display: inline-flex; align-items: center; justify-content: center;
  width: 16px; height: 16px; border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: #fff; font-size: 9px; font-weight: 800;
}
.kt-peer__status { font-size: 12px; color: #10b981; font-weight: 500; }
.kt-peer__status > span { display: inline-flex; align-items: center; gap: 4px; }

.kt-typing-ind { color: #ff4d8d; font-weight: 600; font-style: italic; display: inline-flex; align-items: center; gap: 4px; }
.kt-typing-ind > span {
  width: 4px; height: 4px; border-radius: 50%; background: currentColor; display: inline-block;
  animation: ktDot 1.2s ease-in-out infinite;
}
.kt-typing-ind > span:nth-child(2) { animation-delay: .15s; }
.kt-typing-ind > span:nth-child(3) { animation-delay: .3s; }
@keyframes ktDot {
  0%, 60%, 100% { transform: scale(.6); opacity: .3; }
  30% { transform: scale(1.3); opacity: 1; }
}

.kt-actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.kt-icon {
  width: 38px; height: 38px; border-radius: 12px;
  background: #fff; border: 1px solid rgba(26,16,51,.08); color: #4a3d66;
  display: flex; align-items: center; justify-content: center;
  transition: border-color .2s, color .2s, transform .15s;
}
.kt-icon:hover { border-color: #ff4d8d; color: #ff4d8d; transform: translateY(-1px); }

/* ===== Messages ===== */
.kt-messages {
  flex: 1; overflow-y: auto;
  padding: 14px 14px 8px;
  scroll-behavior: smooth;
}
.kt-messages::-webkit-scrollbar { width: 6px; }
.kt-messages::-webkit-scrollbar-thumb { background: rgba(26,16,51,.15); border-radius: 3px; }

.kt-match-banner {
  display: flex; align-items: center; gap: 14px;
  padding: 14px 18px; margin-bottom: 16px;
  background: linear-gradient(135deg, rgba(255,77,141,.1), rgba(160,32,240,.1));
  border: 1px solid rgba(255,77,141,.25);
  border-radius: 18px;
}
.kt-match-banner__hearts {
  width: 44px; height: 44px; border-radius: 50%;
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  display: flex; align-items: center; justify-content: center;
  font-size: 22px; flex-shrink: 0;
  box-shadow: 0 6px 14px rgba(255,77,141,.35);
  animation: ktBeat 1.5s ease-in-out infinite;
}
@keyframes ktBeat { 0%,100% { transform: scale(1); } 50% { transform: scale(1.1); } }
.kt-match-banner strong { display: block; font-size: 14px; color: #2a1a3f; }
.kt-match-banner small { font-size: 12px; color: #5a4a78; }

.kt-date {
  display: flex; align-items: center; justify-content: center;
  margin: 16px 0 10px;
}
.kt-date span {
  font-size: 11px; font-weight: 700; color: #8273a0;
  letter-spacing: .1em; text-transform: uppercase;
  padding: 4px 12px; border-radius: 999px;
  background: rgba(255,77,141,.08);
}

/* Rows & bubbles */
.kt-row {
  display: flex; gap: 8px; align-items: flex-end;
  margin-bottom: 2px; max-width: 100%;
}
.kt-row--theirs { justify-content: flex-start; }
.kt-row--mine { justify-content: flex-end; }
.kt-row--first { margin-top: 8px; }

.kt-row__avatar {
  width: 30px; height: 30px; flex-shrink: 0;
}
.kt-row__avatar img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; }
.kt-row__avatar--ghost { visibility: hidden; }

.kt-bubble-wrap {
  display: flex; flex-direction: column; min-width: 0;
  max-width: 76%;
}
.kt-row--mine .kt-bubble-wrap { align-items: flex-end; }

.kt-bubble {
  padding: 10px 14px;
  border-radius: 20px;
  font-size: 14px; line-height: 1.4;
  word-wrap: break-word; word-break: break-word;
  position: relative;
  max-width: 100%;
}
.kt-row--theirs .kt-bubble {
  background: #fff;
  color: #2a1a3f;
  border: 1px solid rgba(255,77,141,.1);
  border-bottom-left-radius: 6px;
  box-shadow: 0 2px 8px rgba(26,16,51,.04);
}
.kt-row--mine .kt-bubble {
  background: linear-gradient(135deg, #ff4d8d 0%, #a020f0 100%);
  color: #fff;
  border-bottom-right-radius: 6px;
  box-shadow: 0 4px 14px rgba(255,77,141,.25);
}
/* Tail continuation (same-sender) — softer corners */
.kt-row:not(.kt-row--first) .kt-bubble { border-top-left-radius: 10px; }
.kt-row--mine:not(.kt-row--first) .kt-bubble { border-top-left-radius: 20px; border-top-right-radius: 10px; }
.kt-row--mine:not(.kt-row--tail) .kt-bubble { border-bottom-right-radius: 20px; }
.kt-row--theirs:not(.kt-row--tail) .kt-bubble { border-bottom-left-radius: 20px; }

.kt-bubble__meta {
  font-size: 11px; color: #8273a0; margin: 4px 10px 8px;
  display: flex; align-items: center; gap: 4px;
}
.kt-bubble__read { color: #3b82f6; font-weight: 800; }
.kt-bubble__sent { color: #b8a9c9; }

/* Gift bubble */
.kt-bubble__gift {
  display: inline-flex; align-items: center; gap: 10px;
  font-size: 14px;
}
.kt-bubble__gift-emoji {
  font-size: 26px;
  filter: drop-shadow(0 2px 6px rgba(255,255,255,.4));
}
.kt-row--theirs .kt-bubble__gift-emoji { filter: drop-shadow(0 2px 6px rgba(255,77,141,.2)); }

/* Image bubble */
.kt-bubble__img img {
  max-width: 260px; max-height: 260px;
  border-radius: 16px; display: block;
}

/* Typing bubble */
.kt-bubble--typing { padding: 12px 16px; }
.kt-typing { display: inline-flex; align-items: center; gap: 4px; }
.kt-typing > span {
  width: 6px; height: 6px; border-radius: 50%; background: #b8a9c9;
  animation: ktDot 1.2s ease-in-out infinite;
}
.kt-typing > span:nth-child(2) { animation-delay: .15s; }
.kt-typing > span:nth-child(3) { animation-delay: .3s; }

/* ===== Composer ===== */
.kt-composer {
  flex-shrink: 0;
  display: flex; align-items: flex-end; gap: 8px;
  padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0));
  background: rgba(255,255,255,.92);
  backdrop-filter: blur(22px) saturate(170%);
  -webkit-backdrop-filter: blur(22px) saturate(170%);
  border-top: 1px solid rgba(255,77,141,.1);
}
.kt-icon--attach, .kt-icon--gift {
  width: 40px; height: 40px; border-radius: 50%;
  flex-shrink: 0;
  background: #fff; border: 1px solid rgba(26,16,51,.08);
  color: #a020f0; font-size: 20px;
  display: flex; align-items: center; justify-content: center;
  transition: border-color .2s, transform .15s;
}
.kt-icon--attach:hover, .kt-icon--gift:hover { border-color: #ff4d8d; transform: translateY(-1px); }
.kt-icon--gift { font-size: 20px; }

.kt-input {
  flex: 1; min-width: 0;
  display: flex; align-items: flex-end; gap: 6px;
  background: #fff; border: 1.5px solid rgba(255,77,141,.14);
  border-radius: 22px;
  padding: 8px 10px 8px 16px;
  transition: border-color .2s, box-shadow .2s;
}
.kt-input:focus-within { border-color: #ff4d8d; box-shadow: 0 0 0 4px rgba(255,77,141,.1); }
.kt-input textarea {
  flex: 1; border: none; outline: none; resize: none;
  background: transparent; color: #2a1a3f;
  font-family: inherit; font-size: 14px; line-height: 1.4;
  padding: 4px 0;
  max-height: 120px;
}
.kt-input textarea::placeholder { color: #b8a9c9; }
.kt-emoji {
  background: none; border: none; cursor: pointer;
  color: #8273a0; padding: 4px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  transition: color .2s;
}
.kt-emoji:hover { color: #ff4d8d; }

.kt-send {
  width: 42px; height: 42px; border-radius: 50%;
  border: none; cursor: pointer; flex-shrink: 0;
  background: linear-gradient(135deg, #ff4d8d, #a020f0); color: #fff;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 8px 18px rgba(255,77,141,.35);
  transition: transform .15s, box-shadow .2s;
}
.kt-send:hover { transform: translateY(-2px); box-shadow: 0 12px 24px rgba(255,77,141,.5); }
.kt-send:active { transform: translateY(0); }
.kt-send--mic {
  background: #fff; color: #a020f0;
  border: 1.5px solid rgba(255,77,141,.25);
  box-shadow: 0 4px 10px rgba(26,16,51,.06);
}
.kt-send--mic:hover { border-color: #ff4d8d; box-shadow: 0 8px 16px rgba(255,77,141,.2); }

/* ===== Emoji sheet ===== */
.kt-emoji-sheet {
  position: fixed; inset: 0; z-index: 20;
  background: rgba(26,16,51,.4); backdrop-filter: blur(6px);
  display: flex; align-items: flex-end; justify-content: center;
}
.kt-emoji-sheet__body {
  width: 100%; max-width: 560px; background: #fff;
  border-radius: 24px 24px 0 0; padding: 18px 18px 24px;
  box-shadow: 0 -20px 50px rgba(0,0,0,.2);
  animation: ktSlideUp .25s cubic-bezier(.2,.9,.3,1);
}
@keyframes ktSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
.kt-emoji-sheet__head {
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;
}
.kt-emoji-sheet__head strong { font-size: 15px; }
.kt-emoji-sheet__head button { background: none; border: none; font-size: 18px; color: #8273a0; }
.kt-emoji-sheet__grid {
  display: grid; grid-template-columns: repeat(8, 1fr); gap: 6px;
}
.kt-emoji-sheet__grid button {
  background: #fff9fb; border: none; border-radius: 10px;
  aspect-ratio: 1; font-size: 22px; cursor: pointer;
  transition: background .15s, transform .1s;
}
.kt-emoji-sheet__grid button:hover { background: #ffe8f0; transform: scale(1.1); }
.kt-emoji-sheet-enter-active, .kt-emoji-sheet-leave-active { transition: opacity .2s ease; }
.kt-emoji-sheet-enter, .kt-emoji-sheet-leave-to { opacity: 0; }

/* ===== Info drawer ===== */
.kt-info {
  position: fixed; inset: 0; z-index: 20;
  background: rgba(26,16,51,.45); backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center; padding: 20px;
}
.kt-info__panel {
  position: relative;
  width: 100%; max-width: 400px;
  background: linear-gradient(180deg, #fff 0%, #fff6fa 100%);
  border-radius: 24px; padding: 36px 26px 26px;
  box-shadow: 0 30px 70px rgba(0,0,0,.3);
  text-align: center;
  animation: ktPop .3s cubic-bezier(.2,.9,.3,1.2);
}
@keyframes ktPop { from { opacity: 0; transform: scale(.9); } to { opacity: 1; transform: scale(1); } }
.kt-info__close {
  position: absolute; top: 14px; right: 14px;
  width: 32px; height: 32px; border-radius: 50%;
  background: rgba(26,16,51,.06); border: none; color: #6b5c85;
  font-size: 16px;
}
.kt-info__avatar {
  width: 100px; height: 100px; border-radius: 50%; object-fit: cover;
  border: 4px solid #fff;
  box-shadow: 0 0 0 3px rgba(255,77,141,.35), 0 10px 24px rgba(160,32,240,.2);
}
.kt-info__name {
  font-size: 22px; font-weight: 800; margin: 14px 0 4px; color: #2a1a3f;
  display: flex; align-items: center; justify-content: center; gap: 6px;
}
.kt-info__tick {
  width: 20px; height: 20px; border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #2563eb); color: #fff; font-size: 11px; font-weight: 800;
  display: inline-flex; align-items: center; justify-content: center;
}
.kt-info__meta { font-size: 13px; color: #8273a0; }
.kt-info__bio { font-size: 14px; color: #4a3d66; margin: 14px 0 20px; line-height: 1.5; }
.kt-info__actions { display: flex; flex-direction: column; gap: 8px; }
.kt-info__btn {
  width: 100%; padding: 12px; border: none; border-radius: 12px; cursor: pointer;
  background: linear-gradient(135deg, rgba(255,77,141,.08), rgba(160,32,240,.08));
  color: #a020f0; font-weight: 700; font-size: 14px;
  transition: background .2s;
}
.kt-info__btn:hover { background: linear-gradient(135deg, rgba(255,77,141,.15), rgba(160,32,240,.15)); }
.kt-info__btn--danger { color: #dc2626; background: rgba(239,68,68,.08); }
.kt-info__btn--danger:hover { background: rgba(239,68,68,.15); }
.kt-info-enter-active, .kt-info-leave-active { transition: opacity .2s ease; }
.kt-info-enter, .kt-info-leave-to { opacity: 0; }

/* ===== Desktop ===== */
@media (min-width: 960px) {
  .km-thread {
    position: fixed; top: 64px; left: 0; right: 0; bottom: 0;
  }
  .kt-top { padding: 14px 24px; }
  .kt-back, .kt-icon { width: 42px; height: 42px; }
  .kt-peer__avatar { width: 46px; height: 46px; }
  .kt-peer__name { font-size: 16px; }
  .kt-messages { padding: 20px max(16px, calc(50% - 480px)) 12px; }
  .kt-match-banner { max-width: 960px; margin: 0 auto 20px; }
  .kt-bubble-wrap { max-width: 60%; }
  .kt-composer { padding: 14px max(16px, calc(50% - 480px)); }
}
</style>
